/**
 * PDF print mutation for 患者リスト(施術師毎) — opens PDF in new tab.
 */

import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { resolveServiceType } from '@/constants/serviceType';
import { printVisitPatients } from '@/services/main/visitPatient';
import type { PrintVisitPatientPayload } from '@/pages/main/visitPatient/types';

/**
 * When the backend rejects a `responseType: 'blob'` request the error body comes
 * back as a Blob. Decode it so we can log/surface the JSON error message.
 */
async function readErrorBlob(err: unknown): Promise<string | null> {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      return await data.text();
    } catch {
      return null;
    }
  }
  return typeof data === 'string' ? data : null;
}

export function usePrintVisitPatientMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    PrintVisitPatientPayload
  >({
    mutationFn: payload =>
      printVisitPatients(tenant, clinicNumber, payload, resolveServiceType(serviceType)),
  });

  const triggerPrint = async (payload: PrintVisitPatientPayload) => {
    const tab = window.open('', '_blank');

    try {
      const blob = await mutateAsync(payload);

      // Defensive: server returned 200 but with a JSON error body (shouldn't happen,
      // but axios won't throw in that case).
      if (blob.type.includes('json')) {
        const text = await blob.text();
        // eslint-disable-next-line no-console
        console.error('[VisitPatient print] unexpected JSON response:', text);
        tab?.close();
        toast.error('印刷に失敗しました。');
        return;
      }

      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      const body = await readErrorBlob(err);
      // eslint-disable-next-line no-console
      console.error('[VisitPatient print] failed:', body ?? err);
      toast.error('印刷に失敗しました。');
    }
  };

  return { triggerPrint, isLoading };
}
